import { AnyAction } from '@reduxjs/toolkit';
import { DFDHeaders } from 'serverTypes/authentication';
import { CacheTypes } from '../mobileCaching';

export type Nullable<T> = T | null;
export type Clients =
  | 'amwell'
  | 'amwellAuthService'
  | 'audit'
  | 'binary'
  | 'billing'
  | 'booking'
  | 'ccd'
  | 'cost'
  | 'costLocal'
  | 'dispatcher'
  | 'eob'
  | 'events'
  | 'eventsGeneration'
  | 'financialAssistance'
  | 'getCare'
  | 'highPriorityItems'
  | 'hipaa'
  | 'imh'
  | 'insurance'
  | 'lowPriorityItems'
  | 'medications'
  | 'messaging'
  | 'mhpStatus'
  | 'myDoctors'
  | 'notifications'
  | 'orchestration'
  | 'profile'
  | 'preference'
  | 'provider'
  | 'scanning'
  | 'testResults'
  | 'tooltips'
  | 'tps'
  | 'triageGyant'
  | 'visitSummary'
  | 'uss'
  | 'webpay';

export interface ActionPayloadRequest {
  method?: 'get' | 'post' | 'put' | 'patch' | 'delete';
  url?: string;
  data?: Record<string | number, any>;
  headers?: Record<string | number, any>;
  params?: Record<string | number, any>;
}

export interface ActionPayload<T = any> {
  cacheRetrievalSuccessAction?: string;
  cacheType?: CacheTypes;
  cacheUpdatedAt?: string;
  data?: T;
  errors?: string[] | Error[];
  client?: Clients;
  useRequestState?: boolean;
  requestStateSelector?: any;
  request?: ActionPayloadRequest;
}

export interface Action<T = any> extends AnyAction {
  type: string;
  payload?: ActionPayload<T>;
  error?: Error;
}

export interface ServiceError {
  code: string;
  errno: string;
  sqlMessage: string;
  sqlState: string;
  index: string;
  sql: string;
}

export interface Coordinate {
  latitude: number | string;
  longitude: number | string;
}

export interface LinkTo {
  pathname: string;
  search?: string;
  hash?: string;
  state?: Record<string, string>;
}

export enum AdapterSource {
  MHP = 'mhp',
  UTAH = 'utah'
}

export enum DispatcherAuthPrefix {
  AUTHENTICATED = '/u',
  PUBLIC = '/p',
  AUTHLITE = '/al'
}

export enum DispatcherCategoryService {
  APP_SETTINGS = '/app-settings-service',
  HEALTH_RECORDS = '/health-record-service',
  SELF = '/self'
}

export enum DispatcherResource {
  ALLERGIES = '/allergies',
  APP_SETTINGS = '/app-settings',
  BINARY = '/binary-contents',
  CLINIC_DOCUMENTS = '/clinic_documents',
  CONDITIONS = '/conditions', // problems list
  ENCOUNTERS_ID = '/encounters/id',
  GOALS = '/goals',
  HEALTH_ADVISORIES = '/health-advisories',
  IMAGING = '/imaging',
  IMMUNIZATIONS = '/immunizations',
  LABS = '/labs',
  MEDICAL_HISTORY = '/medical-history',
  MEDICAL_TEST_RESULTS = '/medical-test-results',
  MEDICATIONS = '/medications',
  MICROBIOLOGIES = '/microbiologies',
  PROCEDURES = '/procedures',
  PATHOLOGIES = '/pathologies',
  SOCIAL_HISTORY = '/social-history',
  VISIT_SUMMARIES = '/visit-summaries',
  VITAL_SIGNS = '/vital-signs'
}

export enum DispatcherEndpoint {
  DOWNLOAD = '/_download',
  GLOSSARY = '/glossary',
  HISTORICAL = '/historical',
  ID = '/id',
  ID_DOWNLOAD = '/id/_download',
  ID_TRANSMIT = '/id/_transmit',
  POLLING_STATUS = '/_polling-status',
  SETTINGS = '/settings',
  SIR = '/sir',
  SIR_EXISTS = '/sir/_exists',
  SIR_TRANSMIT = '/sir/_transmit',
  TRANSMIT = '/_transmit',
  VISIT_SUMMARIES = '/visit-summaries'
}

export enum RequestState {
  INITIALIZED = 'INITIALIZED',
  REQUESTING = 'REQUESTING',
  SUCCESS = 'SUCCESS',
  ERROR = 'ERROR'
}

export interface KeyValueStringMap {
  [key: string]: string;
}

export interface GenericObject {
  [key: string]: any;
}

export type DFDHeadersObject = {
  [K in DFDHeaders]?: string | number;
};

export interface DFDDetailsReq {
  method: string;
  url: string;
  headers?: DFDHeadersObject;
}

export interface MhpDetails {
  url: string;
  contentType?: string;
  headers?: DFDHeadersObject;
}
